import React, { useState } from 'react';
import { PillsInput, Pill, Combobox, CheckIcon, Group, useCombobox, ScrollArea } from '@mantine/core';
import { FilterGroup, VectorSearchFilters } from '../../../lib/types';

type Props = {
  searchFields: string[]
  label: string
  term: FilterGroup
  updatedFilters: VectorSearchFilters
  setUpdatedFilters: React.Dispatch<React.SetStateAction<VectorSearchFilters>>
}

export const SearchablePillInput = ({ searchFields, label, updatedFilters, term, setUpdatedFilters }: Props) => {
  const combobox = useCombobox({
    onDropdownClose: () => {
      combobox.resetSelectedOption()
      setSearch('')
    },
    onDropdownOpen: () => combobox.updateSelectedOptionIndex('active'),
  });

  const [search, setSearch] = useState('');
  const selectedValues = updatedFilters[term]

  const handleValueSelect = (val: string) => {
    if (val.trim().length) {
        if (selectedValues?.includes(val)) {
        setUpdatedFilters(prev => !!prev[term] ? ({ ...prev, [term]: prev[term]?.filter((v) => v !== val) }) : prev)
      } else {
          setUpdatedFilters(prev => ({ ...prev, [term]: !prev[term] ? [val] : [ ...prev[term] as string[], val ] }))
      }
    }
  }

  const handleValueRemove = (val: string) => {
    if (!selectedValues?.length) return

    if (selectedValues.length === 1) {
      setUpdatedFilters({ ...updatedFilters, [term]: null })
    } else {
      setUpdatedFilters({ ...updatedFilters, [term]: selectedValues?.filter((v: string) => v !== val) })
    }
  }

  const options = searchFields
    ?.filter((item) => item?.toLowerCase()?.includes(search.trim()?.toLowerCase()))
    ?.map((item: FilterGroup) => (
      <Combobox.Option value={item} key={item} active={selectedValues?.includes(item)}>
        <Group gap="sm">
          {selectedValues?.includes(item) ? <CheckIcon size={12} /> : null}
          <span>{item}</span>
        </Group>
      </Combobox.Option>
    ));

  return (
    <Combobox store={combobox} onOptionSubmit={handleValueSelect}>
      <Combobox.DropdownTarget>
        <PillsInput label={label} onClick={() => combobox.openDropdown()}>
          <Pill.Group>
            { selectedValues?.map((item) => (
              <Pill size='lg' c='blue' key={item} withRemoveButton onRemove={() => handleValueRemove(item)}>
                {item}
              </Pill>
              ))
            }
            <Combobox.EventsTarget>
              <PillsInput.Field
                onFocus={() => combobox.openDropdown()}
                onBlur={() => combobox.closeDropdown()}
                value={search}
                placeholder={selectedValues?.length ? '' : label}
                onChange={(event) => {
                  if (!combobox.dropdownOpened) combobox.openDropdown()
                  combobox.updateSelectedOptionIndex();
                  setSearch(event.currentTarget.value);
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Backspace' && search.length === 0) {
                    event.preventDefault();
                    if (selectedValues) handleValueRemove(selectedValues[selectedValues.length - 1]);
                  }
                }}
              />
            </Combobox.EventsTarget>
          </Pill.Group>
        </PillsInput>
      </Combobox.DropdownTarget>

        <Combobox.Dropdown>
          <ScrollArea mah={500} style={{ overflow: 'scroll' }}>
            <Combobox.Options>
              {options?.length > 0 ? options : <Combobox.Empty>Nothing found...</Combobox.Empty>}
            </Combobox.Options>
          </ScrollArea>
        </Combobox.Dropdown>
    </Combobox>
  );
}
